import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/pages/labelling/workstation";
import type { ShiftSummary } from "@/hooks/use-labelling";

/** Which way out of the shift is being confirmed. Sign-out ends the shift too — the
 * labeller just did not say so — which is the whole reason it asks (POS2-1093). */
export type EndShiftMode = "end" | "sign-out";

const COPY = {
  end: {
    title: "End shift?",
    description: "Your time and totals below are saved to your record.",
    confirm: "Confirm end shift",
    confirming: "Ending…",
  },
  "sign-out": {
    title: "Sign out and end your shift?",
    description:
      "Signing out ends your shift — the clock stops here. Your time and totals below are saved to your record.",
    confirm: "End shift & sign out",
    confirming: "Signing out…",
  },
} as const;

/**
 * End-shift confirmation. The PRD requires the summary *and* the confirmation —
 * clocking out is what closes the hours record, so it should not be one stray
 * click away.
 *
 * The numbers shown are the server's, taken from the last shift poll rather than
 * counted in the browser: this is the record, and the record is what the labeller
 * should be agreeing to.
 *
 * Sign-out reuses this rather than adding a second dialog, because it is the same
 * decision reached by a smaller button: the same record closes either way, and the
 * only thing that differs is the sentence saying so.
 */
export function EndShiftDialog({
  shift,
  mode,
  isEnding,
  onKeepLabelling,
  onConfirm,
}: {
  shift: ShiftSummary | null;
  mode: EndShiftMode;
  isEnding: boolean;
  onKeepLabelling: () => void;
  onConfirm: () => void;
}) {
  const copy = COPY[mode];
  return (
    <Dialog open={shift !== null} onOpenChange={(open) => !open && onKeepLabelling()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        {shift && (
          <div className="grid grid-cols-2 gap-2.5">
            <Stat label="Duration" value={formatDuration(shift.duration_seconds)} />
            <Stat label="Items labelled" value={String(shift.items_labelled)} />
            <Stat label="Items per hour" value={String(Math.round(shift.items_per_hour))} />
            <Stat label="Target" value={`${shift.items_labelled} / ${shift.target_items}`} />
            <Stat label="Correct" value={String(shift.correct_count)} tone="emerald" />
            <Stat label="Relabelled" value={String(shift.relabel_count)} tone="orange" />
            <Stat label="Discarded" value={String(shift.discard_count)} tone="red" />
            {/* Toneless, because it is not a verdict. It is in the totals above,
                where it counts toward the target like any other item. */}
            <Stat label="Can't verify" value={String(shift.cant_verify_count)} />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onKeepLabelling} disabled={isEnding}>
            Keep labelling
          </Button>
          <Button onClick={onConfirm} disabled={isEnding}>
            {isEnding ? copy.confirming : copy.confirm}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "emerald" | "orange" | "red";
}) {
  return (
    <div className="rounded-lg border border-black/[0.06] px-3.5 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={cn(
          "mt-1 text-base font-semibold tabular-nums text-gray-900",
          tone === "emerald" && "text-emerald-700",
          tone === "orange" && "text-orange-600",
          tone === "red" && "text-red-600",
        )}
      >
        {value}
      </div>
    </div>
  );
}
